import { useState } from "react";
import type { InputType, UpdateInputTypeRequest } from "../../services/inputTypesApi";
import { useUpdateInputType } from "../../hooks/useInputTypes";
import { useConfirm, withConfirmSuccess } from "@/shared/contexts/confirm-context";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import { Edit2 } from "lucide-react";

interface EditInputTypeDialogProps {
  inputType: InputType;
}

export const EditInputTypeDialog = ({
  inputType,
}: EditInputTypeDialogProps) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<UpdateInputTypeRequest>({
    id: inputType.id,
    name: inputType.name,
    description: inputType.description,
    isActive: inputType.isActive,
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const updateMutation = useUpdateInputType();
  const confirm = useConfirm();

  const handleChange = (field: keyof Omit<UpdateInputTypeRequest, 'id'>, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    confirm({
      title: "Save input type changes?",
      description: `Changes to “${inputType.name}” will be saved.`,
      confirmLabel: "Save changes",
      onConfirm: () =>
        withConfirmSuccess(
          updateMutation.mutateAsync(formData),
          "Input type updated"
        )
          .then(() => setOpen(false))
          .catch((error: any) => {
            if (error.response?.data?.errors) {
              setFieldErrors(error.response.data.errors);
            }
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-start">
          <Edit2 className="w-4 h-4 mr-2" />
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Input Type</DialogTitle>
          <DialogDescription>
            Update the input type information
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {updateMutation.error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {updateMutation.error.message}
            </div>
          )}

          <Field>
            <FieldLabel htmlFor="name">
              Name <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              required
              disabled={updateMutation.isPending}
            />
            {fieldErrors.name && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.name.join(", ")}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="description">Description</FieldLabel>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              disabled={updateMutation.isPending}
              className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </Field>

          <Field>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleChange("isActive", e.target.checked)}
                disabled={updateMutation.isPending}
                className="w-4 h-4"
              />
              <span className="text-sm">Active</span>
            </label>
          </Field>

          <Button
            type="submit"
            className="w-full"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? "Updating..." : "Update Input Type"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};